import { curSym } from "@/lib/company";
import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtTL } from "@/components/StatusBadge";
import { BadgePercent, Gift, Plus, Trash2, Power, Pencil, Tag, Layers } from "lucide-react";

const TYPE_LABELS = { percent: "% İndirim", amount: "Tutar İndirimi", bogo: "2 Al 1 Öde (X Al Y Öde)", birthday: "Doğum Günü İndirimi (%)" };
const emptyForm = { name: "", type: "percent", scope: "all", categories: [], value: 10, buy_qty: 2, get_qty: 1, min_basket: 0, window_days: 0, priority: 0, active: true, starts_at: "", ends_at: "" };

export default function Campaigns() {
  const [loy, setLoy] = useState(null);
  const [loySaving, setLoySaving] = useState(false);
  const [camps, setCamps] = useState([]);
  const [cats, setCats] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/campaigns").then((r) => setCamps(r.data || [])).catch(() => {});
    api.get("/loyalty/settings").then((r) => setLoy(r.data)).catch(() => {});
    api.get("/categories").then((r) => {
      const arr = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setCats(arr.map((c) => (typeof c === "string" ? c : c.name)).filter(Boolean));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveLoyalty = async () => {
    setLoySaving(true);
    try { const { data } = await api.put("/loyalty/settings", loy); setLoy(data); toast.success("Sadakat ayarları kaydedildi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setLoySaving(false); }
  };

  const openNew = () => { setForm(emptyForm); setEditId(null); setDialogOpen(true); };
  const openEdit = (c) => {
    setForm({ ...emptyForm, ...c, categories: c.categories || [], starts_at: (c.starts_at || "").slice(0, 10), ends_at: (c.ends_at || "").slice(0, 10) });
    setEditId(c.id); setDialogOpen(true);
  };
  const saveCampaign = async () => {
    setSaving(true);
    try {
      const payload = { ...form, value: Number(form.value) || 0, buy_qty: Number(form.buy_qty) || 1, get_qty: Number(form.get_qty) || 1, min_basket: Number(form.min_basket) || 0 };
      if (editId) await api.put(`/campaigns/${editId}`, payload);
      else await api.post("/campaigns", payload);
      toast.success(editId ? "Kampanya güncellendi" : "Kampanya oluşturuldu");
      setDialogOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };
  const toggle = async (c) => { try { await api.post(`/campaigns/${c.id}/toggle`); load(); } catch { toast.error("Değiştirilemedi"); } };
  const del = async (c) => { if (!window.confirm(`"${c.name}" kampanyası silinsin mi?`)) return; try { await api.delete(`/campaigns/${c.id}`); toast.success("Silindi"); load(); } catch { toast.error("Silinemedi"); } };

  const toggleCat = (name) => setForm((f) => ({ ...f, categories: f.categories.includes(name) ? f.categories.filter((x) => x !== name) : [...f.categories, name] }));

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Kampanyalar & Sadakat</h1>
        <p className="mt-1 text-sm text-slate-500">POS'ta otomatik uygulanan kampanyalar ve müşteri sadakat puanı programı.</p>
      </div>

      {/* Sadakat ayarları */}
      {loy && (
        <div className="rounded-2xl border border-brand-orange/25 bg-brand-orange/5 p-5 shadow-sm" data-testid="loyalty-settings-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2"><Gift className="h-5 w-5 text-brand-orange" /><p className="font-heading text-lg font-bold text-slate-900">Sadakat Puanı Programı</p></div>
            <Switch data-testid="loyalty-enabled" checked={!!loy.enabled} onCheckedChange={(v) => setLoy({ ...loy, enabled: v })} />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div><Label className="text-xs">Kazanım Oranı (%)</Label><Input data-testid="loyalty-earn-pct" type="number" step="0.5" value={loy.earn_pct} onChange={(e) => setLoy({ ...loy, earn_pct: e.target.value })} className="mt-1" /><p className="mt-1 text-[11px] text-slate-400">Ödenen tutarın %'si kadar puan (1 puan = {fmtTL(loy.redeem_rate || 0)}).</p></div>
            <div><Label className="text-xs">Puan Değeri ({curSym()}/puan)</Label><Input data-testid="loyalty-redeem-rate" type="number" step="0.1" value={loy.redeem_rate} onChange={(e) => setLoy({ ...loy, redeem_rate: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Min. Kullanım (puan)</Label><Input data-testid="loyalty-min-redeem" type="number" value={loy.min_redeem} onChange={(e) => setLoy({ ...loy, min_redeem: e.target.value })} className="mt-1" /></div>
          </div>
          <div className="mt-3"><Button data-testid="loyalty-save" onClick={saveLoyalty} disabled={loySaving} className="bg-brand-orange text-white hover:bg-orange-600">Sadakat Ayarlarını Kaydet</Button></div>
        </div>
      )}

      {/* Kampanyalar */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><BadgePercent className="h-5 w-5 text-brand-green" /><p className="font-heading text-lg font-bold text-slate-900">Kampanyalar</p></div>
          <Button data-testid="campaign-new-btn" onClick={openNew} className="bg-brand-green text-white hover:bg-brand-green-600"><Plus className="mr-1.5 h-4 w-4" /> Yeni Kampanya</Button>
        </div>
        {camps.length === 0 ? (
          <p data-testid="campaigns-empty" className="py-8 text-center text-sm text-slate-400">Henüz kampanya yok. "Yeni Kampanya" ile ekleyin — POS'ta otomatik uygulanır.</p>
        ) : (
          <div className="space-y-2">
            {camps.map((c) => (
              <div key={c.id} data-testid={`campaign-${c.id}`} className={`flex items-center justify-between rounded-xl border p-3 ${c.active ? "border-brand-green/30 bg-brand-green/5" : "border-slate-200 bg-slate-50/60"}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-slate-800">{c.name}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{TYPE_LABELS[c.type] || c.type}</span>
                    {c.active ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Aktif</span> : <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-500">Pasif</span>}
                  </div>
                  <p className="mt-0.5 text-[12px] text-slate-500">
                    {c.type === "percent" && `%${c.value} indirim`}
                    {c.type === "amount" && `${fmtTL(c.value)} indirim${c.min_basket ? ` · min ${fmtTL(c.min_basket)}` : ""}`}
                    {c.type === "bogo" && `${c.buy_qty} al ${c.get_qty} bedava`}
                    {c.type === "birthday" && `Doğum günü %${c.value}${c.window_days ? ` (±${c.window_days}g)` : ""}`}
                    {" · "}
                    {c.scope === "all" ? "Tüm ürünler" : c.scope === "category" ? `Kategoriler: ${(c.categories || []).join(", ")}` : "Seçili ürünler"}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button data-testid={`campaign-toggle-${c.id}`} variant="outline" size="sm" onClick={() => toggle(c)} className="border-slate-300"><Power className="h-4 w-4" /></Button>
                  <Button data-testid={`campaign-edit-${c.id}`} variant="outline" size="sm" onClick={() => openEdit(c)} className="border-slate-300"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`campaign-delete-${c.id}`} variant="outline" size="sm" onClick={() => del(c)} className="border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="campaign-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Kampanyayı Düzenle" : "Yeni Kampanya"}</DialogTitle>
            <DialogDescription>POS'ta uygun sepetlere otomatik uygulanır.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Kampanya Adı *</Label><Input data-testid="campaign-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="ör. Hafta Sonu %10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Tür</Label>
                <select data-testid="campaign-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
                  {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Kapsam</Label>
                <select data-testid="campaign-scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
                  <option value="all">Tüm ürünler</option>
                  <option value="category">Kategoriler</option>
                </select>
              </div>
            </div>
            {form.type !== "bogo" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">{["percent", "birthday"].includes(form.type) ? "İndirim (%)" : `İndirim (${curSym()})`}</Label><Input data-testid="campaign-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1" /></div>
                {form.type === "amount" && <div><Label className="text-xs">Min. Sepet ({curSym()})</Label><Input data-testid="campaign-min-basket" type="number" value={form.min_basket} onChange={(e) => setForm({ ...form, min_basket: e.target.value })} className="mt-1" /></div>}
                {form.type === "birthday" && <div><Label className="text-xs">Gün Penceresi (±)</Label><Input data-testid="campaign-window" type="number" value={form.window_days} onChange={(e) => setForm({ ...form, window_days: e.target.value })} className="mt-1" placeholder="0 = tam gün" /></div>}
              </div>
            )}
            {form.type === "birthday" && <p className="-mt-1 text-[11px] text-brand-orange">Yalnızca müşteri seçili ve doğum günü bugüne (± pencere) denk gelen satışlara otomatik uygulanır.</p>}
            {form.type === "bogo" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Kaç Al</Label><Input data-testid="campaign-buy-qty" type="number" value={form.buy_qty} onChange={(e) => setForm({ ...form, buy_qty: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Kaç Bedava</Label><Input data-testid="campaign-get-qty" type="number" value={form.get_qty} onChange={(e) => setForm({ ...form, get_qty: e.target.value })} className="mt-1" /></div>
              </div>
            )}
            {form.scope === "category" && (
              <div>
                <Label className="text-xs flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> Kategoriler</Label>
                <div className="mt-1 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto rounded-md border border-slate-200 p-2" data-testid="campaign-categories">
                  {cats.map((cat) => (
                    <button key={cat} type="button" onClick={() => toggleCat(cat)}
                      className={`rounded-full px-2.5 py-1 text-[12px] font-semibold transition-colors ${form.categories.includes(cat) ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      <Tag className="mr-1 inline h-3 w-3" />{cat}
                    </button>
                  ))}
                  {cats.length === 0 && <span className="text-[12px] text-slate-400">Kategori bulunamadı</span>}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Başlangıç (ops.)</Label><Input data-testid="campaign-starts" type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Bitiş (ops.)</Label><Input data-testid="campaign-ends" type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="mt-1" /></div>
            </div>
            <label className="flex cursor-pointer items-center gap-2"><Switch data-testid="campaign-active" checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><span className="text-sm text-slate-600">Hemen aktif et</span></label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button data-testid="campaign-save" onClick={saveCampaign} disabled={saving} className="bg-brand-green text-white hover:bg-brand-green-600">{saving ? "Kaydediliyor…" : "Kaydet"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
